import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useRbacStore } from './store/rbacStore';
import { useBrandingStore } from './store/brandingStore';
import { AppToaster } from './components/LoadingToast';
import { ErrorBoundary } from './components/ErrorBoundary';
import { RetryBoundary } from './components/RetryBoundary';
import { RequireHubRoute } from './components/RequireHubRoute';
import { Button } from './components/Button';

// Layouts
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';

// Auth Pages
import { Login } from './pages/auth/Login';
import { Register } from './pages/auth/Register';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { AuthCallback } from './pages/auth/AuthCallback';

// Main Pages
import { Dashboard } from './pages/Dashboard';

// Hub Pages
import { WorkHub } from './hubs/work/WorkHub';
import { PeopleHub } from './hubs/people/PeopleHub';
import { MoneyHub } from './hubs/money/MoneyHub';
import { AssetsHub } from './hubs/assets/AssetsHub';
import { SupportHub } from './hubs/support/SupportHub';
import { GrowthHub } from './hubs/growth/GrowthHub';
import { VaultHub } from './hubs/vault/VaultHub';
import { AdminHub } from './hubs/admin/AdminHub';

// Settings Pages
import { Profile } from './pages/settings/Profile';
import { Settings } from './pages/settings/Settings';
import { BusinessSettings } from './pages/settings/BusinessSettings';
import { NotificationsPage } from './pages/notifications/NotificationsPage';

// Error Pages
import { NotFound } from './pages/errors/NotFound';
import { Unauthorized } from './pages/errors/Unauthorized';

// Protected Route Wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const { loadPermissions, isLoaded: rbacLoaded, isLoading: rbacLoading } = useRbacStore();
  
  React.useEffect(() => {
    if (isAuthenticated && !rbacLoaded && !rbacLoading) {
      loadPermissions();
    }
  }, [isAuthenticated, rbacLoaded, rbacLoading, loadPermissions]);

  if (authLoading || rbacLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4 size-8 animate-spin rounded-full border-2 border-border border-b-primary"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
}

// Public / Guest Route Wrapper (redirects authenticated users away from auth pages)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mb-4 size-8 animate-spin rounded-full border-2 border-border border-b-primary"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

// Error Fallback Component
function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md p-8">
        <h1 className="mb-4 font-display text-page-title text-destructive">Something went wrong</h1>
        <p className="mb-6 text-muted-foreground">
          We're sorry for the inconvenience. Please try refreshing the page.
        </p>
        <Button
          onClick={() => window.location.reload()}
        >
          Refresh Page
        </Button>
      </div>
    </div>
  );
}

export default function App() {
  const { initBranding, loadBrandingFromServer } = useBrandingStore();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  useEffect(() => {
    initBranding();
  }, [initBranding]);

  // Once signed in, pull the organization's branding from the database so it
  // reflects everywhere (any device/browser), not just where it was last set.
  useEffect(() => {
    if (isAuthenticated) {
      loadBrandingFromServer();
    }
  }, [isAuthenticated, loadBrandingFromServer]);

  return (
    <ErrorBoundary fallback={<ErrorFallback />}>
      <RetryBoundary maxRetries={3}>
        <Routes>
          {/* Public Routes */}
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/callback" element={<AuthCallback />} />
          <Route
            element={
              <PublicRoute>
                <AuthLayout />
              </PublicRoute>
            }
          >
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          {/* Error Pages */}
          <Route path="/404" element={<NotFound />} />
          <Route path="/401" element={<Unauthorized />} />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Hubs — each gated by role→hub access (migration 54). */}
            <Route path="/people/*" element={<RequireHubRoute hubKey="people"><PeopleHub /></RequireHubRoute>} />
            <Route path="/work/*" element={<RequireHubRoute hubKey="work"><WorkHub /></RequireHubRoute>} />
            <Route path="/money/*" element={<RequireHubRoute hubKey="money"><MoneyHub /></RequireHubRoute>} />
            <Route path="/assets/*" element={<RequireHubRoute hubKey="assets"><AssetsHub /></RequireHubRoute>} />
            <Route path="/support/*" element={<RequireHubRoute hubKey="support"><SupportHub /></RequireHubRoute>} />
            <Route path="/growth/*" element={<RequireHubRoute hubKey="growth"><GrowthHub /></RequireHubRoute>} />
            <Route path="/vault/*" element={<RequireHubRoute hubKey="vault"><VaultHub /></RequireHubRoute>} />
            <Route path="/admin/*" element={<RequireHubRoute hubKey="admin"><AdminHub /></RequireHubRoute>} />

            {/* Notifications */}
            <Route path="/notifications" element={<NotificationsPage />} />

            {/* Settings */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/settings/platform" element={<Navigate to="/admin/access" replace />} />
            <Route path="/settings/business" element={<BusinessSettings />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
        
        <AppToaster />
      </RetryBoundary>
    </ErrorBoundary>
  );
}
